/**
 * @file PlanGovernanceSection.jsx
 * @module ui/src/components/tenant-admin/sections/PlanGovernanceSection
 * @description
 *   Presents the administrative governance envelope of an atomic plan document,
 *   keeping workflow state, effective applicability, amendment provenance,
 *   approval evidence, version lineage, and audit entry points visibly distinct.
 *
 * @version 1.0.1
 * @since S755
 * @author data-fit LLC · DecisionFlow · S775
 *
 * @architecture
 *   Locked decisions : #1, #2, #25
 *   Temporal invariants : INV-3, INV-9
 *   Design principles : DP-1, DP-2, DP-5, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - A plan is governed as one atomic document.
 *   - planStatus represents workflow state only and never establishes temporal applicability.
 *   - Effective applicability is displayed from effectiveFrom/effectiveTo supplied by the server.
 *   - Approved evidence and a proposed draft amendment remain visibly distinct.
 *   - A superseded workflow state does not erase historical governing applicability.
 *   - Version V2+ displays its revision reason when supplied.
 *   - CRL departure rationale is CA-facing governance evidence and is labelled accordingly.
 *   - Full version-history behavior is delegated to C-55 rather than reimplemented here.
 *   - readOnly is a presentation/editability contract and is not a temporal signal.
 *
 * @session-6-migration-flags
 *   - effective_from on plan — pending m_tc_01
 *   - effective_to on plan — pending m_tc_01; nullable/open-ended
 *   - GET /api/v1/plans/{id}/governance contract — Session 6 API gap
 *   - crlOverrideNote/design_source persistence contract — Session 6 governance API/schema alignment required
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group C batch delivery
 *   1.0.1  S756  OI-S755-C51-AUDITSUMMARY-REQUIRED-01 — auditSummary PropType changed
 *                from .isRequired to optional; null-guard added to audit summary card;
 *                fallback "Audit summary not yet available" render added for absent
 *                governance endpoint response. AuditSummary typedef fields relaxed to
 *                optional. Usage example updated to show absent-auditSummary case.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PlanVersionHistory from '../PlanVersionHistory';

/**
 * @typedef {Object} GovernancePayload
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {string} planStatus
 * @property {number} version
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 * @property {'wizard'|'addin'|'webapp'} sourceType
 * @property {'crl'|'crl_override'|'custom'} designSource
 * @property {string|null} crlOverrideNote
 * @property {string|null} submittedBy
 * @property {string|null} submittedAt
 * @property {string|null} approvedBy
 * @property {string|null} approvedAt
 * @property {string|null} revisionReason
 * @property {number|null} supersededBy
 */

/**
 * @typedef {Object} VersionLineage
 * @property {number} version
 * @property {string} planStatus
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 * @property {'created'|'approved'|'amended'|'superseded'|'withdrawn'|'cancelled'} eventType
 * @property {string} eventAt
 */

/**
 * @typedef {Object} AuditSummary
 * @property {number} [totalChanges]
 * @property {string} [lastChangedAt]
 * @property {string} [lastChangedBy]
 */

/**
 * @typedef {Object} PlanGovernanceSectionProps
 * @property {GovernancePayload} plan
 * @property {VersionLineage[]} lineage
 * @property {AuditSummary} auditSummary
 * @property {boolean} readOnly
 * @property {Function} onOpenHistory
 */

const WORKFLOW_LABELS = Object.freeze({
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
});

const SOURCE_LABELS = Object.freeze({
  wizard: 'Plan creation wizard',
  addin: 'Excel add-in',
  webapp: 'Web application',
});

const DESIGN_SOURCE_LABELS = Object.freeze({
  crl: 'CRL recommendation',
  crl_override: 'CRL recommendation with acknowledged departure',
  custom: 'Custom design',
});

const EVENT_LABELS = Object.freeze({
  created: 'Created',
  approved: 'Approved',
  amended: 'Amended',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
});

const styles = {
  root: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  card: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 8,
    background: 'rgb(var(--color-surface))',
    padding: 16,
    marginBottom: 14,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 14,
  },
  label: {
    display: 'block',
    color: 'rgb(var(--color-text-muted))',
    fontSize: 12,
    marginBottom: 3,
  },
  badge: {
    display: 'inline-flex',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 600,
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  lineage: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 12,
  },
  notice: {
    padding: 12,
    borderRadius: 6,
    background: 'rgb(var(--color-surface-muted))',
  },
};

/**
 * WHY: Effective dates are formatting inputs only. No workflow status or fiscal
 * period is allowed to substitute for temporal applicability (INV-3).
 */
function formatDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * WHY: Governance event timestamps describe submission/approval activity and
 * remain conceptually separate from effective applicability (DP-1).
 */
function formatDateTime(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * @param {PlanGovernanceSectionProps} props
 * @returns {JSX.Element}
 */
export default function PlanGovernanceSection({
  plan,
  lineage,
  auditSummary,
  readOnly,
  onOpenHistory,
}) {
  // WHY: Full lineage detail is progressively disclosed because historical
  // evidence must remain accessible without dominating the normal governance
  // summary (DP-5).
  const [lineageExpanded, setLineageExpanded] = useState(false);

  // WHY: readOnly is intentionally not used to infer workflow or temporal state.
  // This surface is governance evidence and currently exposes no editing actions.
  void readOnly;

  const workflowLabel =
    WORKFLOW_LABELS[plan.planStatus] || plan.planStatus;

  const effectiveFrom = formatDate(plan.effectiveFrom);
  const effectiveTo = formatDate(plan.effectiveTo);

  // WHY: DP-2 requires explicit coexistence of a proposed amendment and its
  // governing approved predecessor. This branch concerns document workflow
  // presentation only; it does NOT assign temporal applicability to either
  // version. The authoritative applicability remains the server date window.
  const amendmentDraftInFlight =
    plan.version > 1 && plan.planStatus === 'draft';

  return (
    <section
      style={styles.root}
      aria-labelledby={`plan-governance-heading-${plan.planId}`}
    >
      <header>
        <h2
          id={`plan-governance-heading-${plan.planId}`}
          style={{ marginBottom: 4 }}
        >
          Review &amp; Governance
        </h2>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          Governance record for {plan.planName} · {plan.planCode}
        </p>
      </header>

      {/*
       * WHY: DP-2 forbids collapsing draft amendment state into the approved
       * evidence it proposes to change. The copy explicitly preserves the
       * predecessor as governing while the amendment is under review.
       */}
      {amendmentDraftInFlight && (
        <div
          className="modifier-manager-info"
          style={{ marginBottom: 14 }}
        >
          <strong>Draft amendment in progress.</strong>{' '}
          Approved version V{plan.version - 1} remains governing while this
          amendment is under review.
        </div>
      )}

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Document governance</h3>

        <div style={styles.grid}>
          <div>
            <span style={styles.label}>Version</span>
            <strong>V{plan.version}</strong>
          </div>

          <div>
            <span style={styles.label}>Effective From</span>
            <strong>
              {effectiveFrom || 'Effective period: not yet recorded'}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Effective To</span>
            <strong>
              {!plan.effectiveFrom
                ? 'Effective period: not yet recorded'
                : effectiveTo || 'Open-ended'}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Workflow Status</span>
            <span style={styles.badge}>{workflowLabel}</span>
          </div>

          <div>
            <span style={styles.label}>Source</span>
            <strong>
              {SOURCE_LABELS[plan.sourceType] || plan.sourceType}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Design Source</span>
            <strong>
              {DESIGN_SOURCE_LABELS[plan.designSource] || plan.designSource}
            </strong>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Submission &amp; approval</h3>

        <div style={styles.grid}>
          <div>
            <span style={styles.label}>Submission</span>
            <strong>
              {plan.submittedBy && plan.submittedAt
                ? `Submitted by ${plan.submittedByName || plan.submittedBy} on ${formatDateTime(plan.submittedAt)}`
                : 'Not yet submitted'}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Approval</span>
            <strong>
              {plan.approvedBy && plan.approvedAt
                ? `Approved by ${plan.approvedByName || plan.approvedBy} on ${formatDateTime(plan.approvedAt)}`
                : plan.submittedAt
                  ? 'Pending approval'
                  : 'Not submitted'}
            </strong>
          </div>
        </div>
      </div>

      {/*
       * WHY: revisionReason is amendment provenance for V2+, not general
       * description text. It is therefore rendered only in the amended-version
       * context in which its meaning is unambiguous.
       */}
      {plan.version > 1 && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>Amendment record</h3>

          <div>
            <span style={styles.label}>Amendment reason</span>
            <p style={{ marginTop: 0 }}>
              {plan.revisionReason || 'No amendment reason is recorded.'}
            </p>
          </div>

          {plan.supersededBy && (
            <div>
              <span style={styles.label}>Superseded by plan record</span>
              <p style={{ margin: 0 }}>
                A later plan version exists. This record remains preserved for
                the effective window in which it may govern historical events.
              </p>
            </div>
          )}
        </div>
      )}

      {/*
       * WHY: A CRL override is governance evidence. It remains explicitly
       * labelled as CA-only so it cannot be mistaken for rep-facing plan copy.
       */}
      {plan.designSource === 'crl_override' && plan.crlOverrideNote && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>CRL departure record</h3>
          <div style={styles.notice}>
            <strong>Departure rationale (CA use only):</strong>
            <p style={{ marginBottom: 0 }}>{plan.crlOverrideNote}</p>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Audit summary</h3>

        {/*
         * WHY: auditSummary is sourced from GET /api/v1/plans/{id}/governance, whose
         * contract is not defined until Session 6. The prop is therefore optional.
         * When the endpoint returns a response without an auditSummary block the
         * component must not throw — it falls back to an explicit "not yet available"
         * message so the rest of the governance section remains functional.
         * (OI-S755-C51-AUDITSUMMARY-REQUIRED-01)
         */}
        {auditSummary ? (
          <div style={styles.grid}>
            <div>
              <span style={styles.label}>Recorded field changes</span>
              <strong>{auditSummary.totalChanges.toLocaleString()}</strong>
            </div>

            <div>
              <span style={styles.label}>Last changed</span>
              <strong>{formatDateTime(auditSummary.lastChangedAt) || 'Not recorded'}</strong>
            </div>

            <div>
              <span style={styles.label}>Last changed by</span>
              <strong>{auditSummary.lastChangedBy || 'Not recorded'}</strong>
            </div>
          </div>
        ) : (
          <p className="modifier-manager-readonly-note">
            Audit summary not yet available
          </p>
        )}
      </div>

      <div style={styles.card}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Version lineage</h3>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              The complete document chain remains available, including
              superseded versions.
            </p>
          </div>

          <button
            type="button"
            className="text-button"
            aria-expanded={lineageExpanded}
            aria-controls={`governance-lineage-${plan.planId}`}
            onClick={() => setLineageExpanded((expanded) => !expanded)}
          >
            {lineageExpanded ? 'Hide version lineage' : 'Show version lineage'}
          </button>
        </div>

        {/*
         * WHY: The collapsed preview names each version/event but does not
         * reproduce C-55's audit implementation. C-55 remains the authoritative
         * history component when detailed lineage is requested.
         */}
        <div style={styles.lineage} aria-label="Version lineage summary">
          {lineage.map((entry, index) => (
            <React.Fragment key={`${entry.version}-${entry.eventType}`}>
              {index > 0 && <span aria-hidden="true">→</span>}
              <span style={styles.badge}>
                V{entry.version} ·{' '}
                {EVENT_LABELS[entry.eventType] || entry.eventType}
              </span>
            </React.Fragment>
          ))}
        </div>

        {lineageExpanded && (
          <div
            id={`governance-lineage-${plan.planId}`}
            style={{ marginTop: 14 }}
          >
            <PlanVersionHistory
              planId={plan.planId}
              currentVersion={plan.version}
              compact
            />
          </div>
        )}

        <button
          type="button"
          className="secondary-button"
          onClick={onOpenHistory}
          style={{ marginTop: 14 }}
        >
          View full version history
        </button>
      </div>
    </section>
  );
}

PlanGovernanceSection.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.number.isRequired,
    planCode: PropTypes.string.isRequired,
    planName: PropTypes.string.isRequired,
    planStatus: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string,
    effectiveTo: PropTypes.string,
    sourceType: PropTypes.oneOf(['wizard', 'addin', 'webapp']).isRequired,
    designSource: PropTypes.oneOf(['crl', 'crl_override', 'custom']).isRequired,
    crlOverrideNote: PropTypes.string,
    submittedBy: PropTypes.string,
    submittedByName: PropTypes.string,
    submittedAt: PropTypes.string,
    approvedBy: PropTypes.string,
    approvedByName: PropTypes.string,
    approvedAt: PropTypes.string,
    revisionReason: PropTypes.string,
    supersededBy: PropTypes.number,
  }).isRequired,
  lineage: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.number.isRequired,
      planStatus: PropTypes.string.isRequired,
      effectiveFrom: PropTypes.string,
      effectiveTo: PropTypes.string,
      eventType: PropTypes.oneOf([
        'created',
        'approved',
        'amended',
        'superseded',
        'withdrawn',
        'cancelled',
      ]).isRequired,
      eventAt: PropTypes.string.isRequired,
    }),
  ).isRequired,
  // WHY: auditSummary is optional because the governance endpoint that supplies it
  // is not contracted until Session 6. The render branch null-guards its absence.
  // (OI-S755-C51-AUDITSUMMARY-REQUIRED-01)
  auditSummary: PropTypes.shape({
    totalChanges: PropTypes.number,
    lastChangedAt: PropTypes.string,
    lastChangedBy: PropTypes.string,
  }),
  readOnly: PropTypes.bool.isRequired,
  onOpenHistory: PropTypes.func.isRequired,
};

/**
 * @example
 * // Standard usage — governance endpoint has returned a full auditSummary block:
 * <PlanGovernanceSection
 *   plan={governancePayload}
 *   lineage={versionLineage}
 *   auditSummary={{
 *     totalChanges: 7,
 *     lastChangedAt: '2026-08-12T12:40:00Z',
 *     lastChangedBy: 'Alex Morgan',
 *   }}
 *   readOnly={true}
 *   onOpenHistory={() => openPlanHistoryDrawer(governancePayload.planId)}
 * />
 *
 * @example
 * // Pre-Session-6 usage — governance endpoint has not yet returned auditSummary.
 * // The audit summary card renders "Audit summary not yet available" gracefully.
 * // (OI-S755-C51-AUDITSUMMARY-REQUIRED-01)
 * <PlanGovernanceSection
 *   plan={governancePayload}
 *   lineage={versionLineage}
 *   readOnly={true}
 *   onOpenHistory={() => openPlanHistoryDrawer(governancePayload.planId)}
 * />
 */

/*
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #1: governance is presented for one atomic plan document.
 * - Locked decision #25: later/superseding versions do not erase historical evidence.
 * - INV-3 / DP-1: planStatus is workflow metadata; effective dates are independent fields.
 * - INV-9: this section displays immutable governance evidence and performs no direct write.
 * - DP-2: when a V2+ document is a draft amendment, the prior approved version is explicitly
 *   named as remaining governing while the amendment is under review.
 * - CRL override rationale is labelled "Departure rationale (CA use only)".
 * - C-55 owns detailed version-history behavior; C-51 does not duplicate its audit API.
 * - onOpenHistory delegates the full-history presentation to the parent.
 * - No fiscal-period fallback is used for effective applicability.
 * - auditSummary is optional (OI-S755-C51-AUDITSUMMARY-REQUIRED-01): governance endpoint
 *   contract is undefined until Session 6; absent auditSummary renders a graceful fallback
 *   rather than throwing. The remainder of the governance section is unaffected.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - plan.effective_from — pending m_tc_01
 * - plan.effective_to — pending m_tc_01; nullable/open-ended
 * - GET /api/v1/plans/{id}/governance — endpoint contract not yet defined; Session 6 API gap
 * - crlOverrideNote/design_source persistence — requires Session 6 governance contract alignment
 */

/*
 * ACCESSIBILITY NOTES
 * - Section is labelled by a persistent heading.
 * - Lineage disclosure uses aria-expanded and aria-controls.
 * - Full-history control has the explicit label "View full version history".
 * - Workflow/event states are always visible as text, not communicated by color alone.
 * - Decorative lineage arrows are hidden from assistive technology.
 */
